export type StageKey =
  | "investigate"
  | "triage"
  | "card"
  | "plan"
  | "execution"
  | "resolution";

export interface Stage {
  key: StageKey;
  label: string;
  short: string;
}

export const STAGES: Stage[] = [
  { key: "investigate", label: "Investigate",  short: "Investigate" },
  { key: "triage",      label: "Triage",       short: "Triage" },
  { key: "card",        label: "Triage card",  short: "Triage card" },
  { key: "plan",        label: "Action plan",  short: "Plan & approve" },
  { key: "execution",   label: "Execution",    short: "Execute" },
  { key: "resolution",  label: "Resolution",   short: "Resolve" },
];

interface Props {
  current: StageKey;
  reachedMax: StageKey; // furthest stage the user has reached so far
  onNavigate: (key: StageKey) => void;
}

export function ProgressStepper({ current, reachedMax, onNavigate }: Props) {
  const currentIdx = STAGES.findIndex((s) => s.key === current);
  const reachedIdx = STAGES.findIndex((s) => s.key === reachedMax);

  return (
    <div className="stepper" role="navigation" aria-label="Incident workflow">
      {STAGES.map((stage, i) => {
        const reached = i <= reachedIdx;
        const isCurrent = i === currentIdx;
        const isPast = i < currentIdx;
        const isFuture = i > reachedIdx;
        const canNavigate = reached && !isCurrent;

        return (
          <button
            key={stage.key}
            type="button"
            className={[
              "stepper-step",
              isCurrent && "current",
              isPast && "past",
              isFuture && "future",
              canNavigate && "clickable",
            ]
              .filter(Boolean)
              .join(" ")}
            onClick={canNavigate ? () => onNavigate(stage.key) : undefined}
            disabled={!canNavigate}
            aria-current={isCurrent ? "step" : undefined}
          >
            <span className="stepper-num" aria-hidden="true">
              {isPast ? (
                <CheckIcon />
              ) : (
                <span className="num">{i + 1}</span>
              )}
            </span>
            <span className="stepper-label">{stage.short}</span>
            {i < STAGES.length - 1 && (
              <span
                className={`stepper-connector ${i < reachedIdx ? "filled" : ""}`}
                aria-hidden="true"
              />
            )}
          </button>
        );
      })}
    </div>
  );
}

function CheckIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
      <path
        d="M3 7l3 3 5-6"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
